import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/badge";

import type { Deliverables } from "./EditSizeModal";

import { EditButton } from "./EditButton";
import { EditSizeModal } from "./EditSizeModal";
import { RemoveSizeDialog } from "./RemoveSizeDialog";
import { SidebarCard } from "./SidebarCard";
import { sizeIcon } from "./size-presets";

type DeliverablesCardProps = {
  fileSizes: Deliverables["fileSizes"];
  fileTypes: Deliverables["fileTypes"];
  /** Persists the edited deliverables. When omitted the card is read-only. */
  onChange?: (deliverables: Deliverables) => void;
};

function summarize({ fileSizes, fileTypes }: Deliverables): string {
  const parts = [
    fileSizes.length > 0 &&
      `${fileSizes.length} ${fileSizes.length === 1 ? "size" : "sizes"}`,
    fileTypes.length > 0 && fileTypes.join(", "),
  ].filter(Boolean);

  return parts.join(" · ");
}

export function DeliverablesCard({
  fileSizes,
  fileTypes,
  onChange,
}: DeliverablesCardProps) {
  const [sizes, setSizes] = useState(fileSizes);
  const [types, setTypes] = useState(fileTypes);
  const [editOpen, setEditOpen] = useState(false);
  const [removeIndex, setRemoveIndex] = useState<null | number>(null);

  const editable = Boolean(onChange);

  useEffect(() => {
    setSizes(fileSizes);
    setTypes(fileTypes);
  }, [fileSizes, fileTypes]);

  const commit = (next: Deliverables) => {
    setSizes(next.fileSizes);
    setTypes(next.fileTypes);
    onChange?.(next);
  };

  const summary = summarize({ fileSizes: sizes, fileTypes: types });

  return (
    <SidebarCard>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-text-primary">Deliverables</h2>
        {editable ? (
          <EditButton label="deliverables" onPress={() => setEditOpen(true)} />
        ) : null}
      </div>

      <div className="mt-3 flex flex-col gap-5">
        {summary ? (
          <p className="text-[15px] font-semibold leading-snug text-text-primary">
            {summary}
          </p>
        ) : null}

        <div>
          <span className="text-sm font-semibold text-text-secondary">
            Sizes needed <span className="text-text-error-primary">*</span>
          </span>
          {sizes.length === 0 ? <NotSetYet /> : null}
          <div className="mt-2 grid grid-cols-2 gap-3">
            {sizes.map((item, i) => {
              const Icon = sizeIcon(item.size);

              return (
                <div
                  className="group relative min-w-0 rounded-xl border border-border-primary p-3"
                  key={`${item.label}-${item.size}-${i}`}
                >
                  {editable ? (
                    <button
                      aria-label={`Remove ${item.label} ${item.size}`}
                      className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full text-fg-quaternary opacity-0 transition-opacity hover:text-text-error-primary focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                      onClick={() => setRemoveIndex(i)}
                      type="button"
                    >
                      <X className="size-3.5" />
                    </button>
                  ) : null}
                  <Icon className="size-[18px] text-fg-quaternary" />
                  <p className="mt-2 break-words pr-4 text-sm font-semibold text-text-primary">
                    {item.label}
                  </p>
                  <p className="mt-0.5 break-all text-[13px] text-text-tertiary">
                    {item.size}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <span className="text-sm font-semibold text-text-secondary">
            File types <span className="text-text-error-primary">*</span>
          </span>
          {types.length === 0 ? <NotSetYet /> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            {types.map((type) => (
              <Badge isRounded key={type} size="medium" variant="purple">
                {type}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {editable ? (
        <>
          <EditSizeModal
            isOpen={editOpen}
            onOpenChange={setEditOpen}
            onSave={commit}
            value={{ fileSizes: sizes, fileTypes: types }}
          />
          <RemoveSizeDialog
            isOpen={removeIndex !== null}
            onConfirm={() => {
              if (removeIndex !== null) {
                commit({
                  fileSizes: sizes.filter((_, i) => i !== removeIndex),
                  fileTypes: types,
                });
              }
            }}
            onOpenChange={(open) => {
              if (!open) setRemoveIndex(null);
            }}
            size={removeIndex === null ? undefined : sizes[removeIndex]}
          />
        </>
      ) : null}
    </SidebarCard>
  );
}

function NotSetYet() {
  return <p className="mt-1 text-sm italic text-text-tertiary">Not set yet</p>;
}
